import React, { useState, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal, Dropdown } from 'react-bootstrap'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from 'helpers/helpers'

const OperatorProfileToggle = forwardRef(function OperatorProfileToggle(
	{ children, onClick, className = '' },
	ref
) {
	return (
		<div
			ref={ref}
			className={className}
			onClick={(e) => {
				e.preventDefault()
				onClick(e)
			}}>
			{children}
		</div>
	)
})

export default function OperatorReportBlock({ chipClassName = '', onLogout = null }) {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [modalLogout, setModalLogout] = useState(false)
	const [modalOperatorReport, setModalOperatorReport] = useState(false)
	const [operatorBlock, setOperatorBlock] = useState('x_report')
	const [operatorReport, setOperatorReport] = useState(null)
	const [operatorCheques, setOperatorCheques] = useState({ data: [], total: 0, total_cheques_amount: 0 })
	const [operatorChequesPage, setOperatorChequesPage] = useState(0)
	const [operatorChequesPagination, setOperatorChequesPagination] = useState({
		url: '/cheques',
		response: null,
		size: 20,
	})
	const [operatorFilter, setOperatorFilter] = useState({
		start_date: formatDate(new Date(), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
	})

	async function loadOperatorReport(filter = operatorFilter) {
		var urlParams = ''
		if (filter.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filter.start_date
		if (filter.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filter.end_date

		const response = await GET(`/report-operator${urlParams}`, {}, { loader: true })
		if (httpOk(response)) setOperatorReport(response.data)
	}

	async function loadOperatorCheques(filter = operatorFilter, page = 0) {
		var urlParams = '?status=10'
		if (reduxAccount?.login)
			urlParams += returnSign(urlParams) + 'operator_login=' + encodeURIComponent(reduxAccount.login)
		if (filter.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filter.start_date
		if (filter.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filter.end_date
		if (page > 0)
			urlParams += returnSign(urlParams) + 'page=' + page

		const response = await GET(`/cheques${urlParams}`, {}, { loader: true })
		if (httpOk(response)) {
			setOperatorCheques(response.data)
			setOperatorChequesPage(page)
			setOperatorChequesPagination({
				url: `/cheques${urlParams}`,
				response: response.data,
				size: 20,
			})
		}
	}

	async function toggleModalOperatorReport(bool = false) {
		if (bool) {
			const todayFilter = {
				start_date: formatDate(new Date(), 'yyyy-MM-dd'),
				end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			}
			setOperatorFilter(todayFilter)
			setOperatorBlock('x_report')
			setOperatorChequesPage(0)
			setOperatorChequesPagination({ url: '/cheques', response: null, size: 20 })
			setModalOperatorReport(true)
			await loadOperatorReport(todayFilter)
		} else {
			setModalOperatorReport(false)
			setOperatorReport(null)
			setOperatorCheques({ data: [], total: 0, total_cheques_amount: 0 })
			setOperatorChequesPage(0)
			setOperatorChequesPagination({ url: '/cheques', response: null, size: 20 })
		}
	}

	async function applyOperatorFilter() {
		if (operatorBlock === 'cheques') {
			await loadOperatorCheques(operatorFilter, 0)
		} else {
			await loadOperatorReport(operatorFilter)
		}
	}

	async function selectOperatorBlock(block) {
		setOperatorBlock(block)
		if (block === 'cheques') {
			await loadOperatorCheques(operatorFilter, 0)
		} else if (!operatorReport) {
			await loadOperatorReport(operatorFilter)
		}
	}

	async function paginateOperatorCheques(page) {
		await loadOperatorCheques(operatorFilter, page)
	}

	return (
		<>
			<Dropdown align="end">
				<Dropdown.Toggle
					as={OperatorProfileToggle}
					className={`operator-profile-chip ${chipClassName}`.trim()}>
					<div className="operator-profile-avatar">
						{(reduxAccount.first_name || reduxAccount.login || '?').charAt(0).toUpperCase()}
					</div>
					<div className="operator-profile-text">
						<span className="operator-profile-login">{reduxAccount.login}</span>
						<span className="operator-profile-name">{reduxAccount.first_name}</span>
					</div>
				</Dropdown.Toggle>
				<Dropdown.Menu className="operator-profile-menu">
					<Dropdown.Item onClick={() => toggleModalOperatorReport(true)}>
						<i className="uil uil-chart-line me-2"></i>
						X {t('report')}
					</Dropdown.Item>
					{typeof onLogout === 'function' &&
						<>
							<Dropdown.Divider />
							<Dropdown.Item className="text-danger" onClick={() => setModalLogout(true)}>
								<i className="uil uil-sign-out-alt me-2"></i>
								{t('logout')}
							</Dropdown.Item>
						</>
					}
				</Dropdown.Menu>
			</Dropdown>

			<Modal show={modalLogout} animation={false}
				centered dialogClassName="update-modal-width"
				onHide={() => setModalLogout(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('logout')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{t('are_you_sure_you_want_logout')}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => setModalLogout(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => {
								setModalLogout(false)
								onLogout()
							}}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>

			<Modal show={modalOperatorReport} animation={false} centered size="xl"
				onHide={() => toggleModalOperatorReport(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('my_report')}: {reduxAccount.first_name || reduxAccount.login}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex flex-wrap align-items-end gap-2 mb-3">
						<div className="form-group mb-0" style={{ minWidth: '328px' }}>
							<label>{t('period')}</label>
							<PeriodPicker
								startDate={operatorFilter.start_date}
								endDate={operatorFilter.end_date}
								onChange={(period) => setOperatorFilter({ ...operatorFilter, ...period })}
								withTime={false}
							/>
						</div>
						<button type="button" className="btn btn-primary" onClick={applyOperatorFilter}>
							{t('apply')}
						</button>
					</div>

					<div className="operator-report-body">
						<div className="operator-report-left">
							{[
								{ value: 'x_report', name: `X ${t('report')}` },
								{ value: 'payment_types', name: t('payment_types') },
								{ value: 'products', name: t('products') },
								{ value: 'cheques', name: t('closed_cheques') },
							].map((item) => (
								<div
									key={item.value}
									className={`operator-report-nav-item ${operatorBlock === item.value ? 'active' : ''}`}
									onClick={() => selectOperatorBlock(item.value)}>
									{item.name}
								</div>
							))}
						</div>

						<div className="operator-report-right">
							{operatorBlock === 'x_report' &&
								<div className="operator-report-summary">
									<div className="operator-report-row">
										<span>{t('cheques')} [{t('delivery')}]</span>
										<span>{formatMoney(operatorReport?.summary?.count_delivery)}</span>
									</div>
									<div className="operator-report-row">
										<span>{t('cheques')} [{t('takeaway')}]</span>
										<span>{formatMoney(operatorReport?.summary?.count_takeaway)}</span>
									</div>
									<div className="operator-report-row operator-report-row--bold">
										<span>{t('cheques')}</span>
										<span>{formatMoney(operatorReport?.summary?.count_cheques)}</span>
									</div>
									<div className="operator-report-divider"></div>
									<div className="operator-report-row">
										<span>{t('products')}</span>
										<span>{formatMoney(operatorReport?.summary?.total_products)}</span>
									</div>
									<div className="operator-report-row">
										<span>{t('avg_cheque')}</span>
										<span>{formatMoney(operatorReport?.summary?.avg_cheque)}</span>
									</div>
									<div className="operator-report-row operator-report-row--bold">
										<span>{t('amount')}</span>
										<span>{formatMoney(operatorReport?.summary?.total_amount)}</span>
									</div>
								</div>
							}

							{operatorBlock === 'payment_types' &&
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th>{t('payment_types')}</th>
											<th className="text-center">{t('quantity')}</th>
											<th className="text-end">{t('amount')}</th>
										</tr>
									</thead>
									<tbody>
										{operatorReport?.payment_types?.length
											? operatorReport.payment_types.map((item, index) => (
												<tr key={index}>
													<td>{item.name}</td>
													<td className="text-center">{formatMoney(item.count_cheques)}</td>
													<td className="text-end">{formatMoney(item.amount)}</td>
												</tr>
											))
											: (
												<tr>
													<td colSpan={3} className="text-center text-muted">{t('list_empty')}</td>
												</tr>
											)
										}
									</tbody>
									{!!operatorReport?.payment_types?.length &&
										<tfoot>
											<tr>
												<td><b>{t('total')}</b></td>
												<td className="text-center">
													<b>{formatMoney(operatorReport.payment_types.reduce((sum, item) => sum + Number(item.count_cheques || 0), 0))}</b>
												</td>
												<td className="text-end">
													<b>{formatMoney(operatorReport.payment_types.reduce((sum, item) => sum + Number(item.amount || 0), 0))}</b>
												</td>
											</tr>
										</tfoot>
									}
								</table>
							}

							{operatorBlock === 'products' &&
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ width: '40px' }}>#</th>
											<th>{t('products')}</th>
											<th className="text-center">{t('quantity')}</th>
											<th className="text-end">{t('amount')}</th>
										</tr>
									</thead>
									<tbody>
										{operatorReport?.products?.length
											? operatorReport.products.map((item, index) => (
												<tr key={index}>
													<td>{index + 1}</td>
													<td>{item.name}</td>
													<td className="text-center">{formatMoney(item.quantity)}</td>
													<td className="text-end">{formatMoney(item.amount)}</td>
												</tr>
											))
											: (
												<tr>
													<td colSpan={4} className="text-center text-muted">{t('list_empty')}</td>
												</tr>
											)
										}
									</tbody>
									{!!operatorReport?.products?.length &&
										<tfoot>
											<tr>
												<td colSpan={2}><b>{t('total')}</b></td>
												<td className="text-center">
													<b>{formatMoney(operatorReport.products.reduce((sum, item) => sum + Number(item.quantity || 0), 0))}</b>
												</td>
												<td className="text-end">
													<b>{formatMoney(operatorReport.products.reduce((sum, item) => sum + Number(item.amount || 0), 0))}</b>
												</td>
											</tr>
										</tfoot>
									}
								</table>
							}

							{operatorBlock === 'cheques' &&
								<>
									<table className="table table-bordered table-hover mb-0">
										<thead>
											<tr>
												<th style={{ minWidth: '33px' }} className="text-center">#</th>
												<th>{t('pos')}</th>
												<th className="text-center">{t('cheque')} №</th>
												<th className="text-center">{t('open_date')}</th>
												<th className="text-end">{t('total')}</th>
											</tr>
										</thead>
										<tbody>
											{operatorCheques?.data?.length
												? operatorCheques.data.map((item, index) => (
													<tr key={item.id || index}>
														<td className="text-center">{item.row_number || (operatorChequesPage * 20 + index + 1)}</td>
														<td>{item.pos_short_name || item.pos_name}</td>
														<td className="text-center">{item.cheque_number}</td>
														<td className="text-center">{formatDate(item.cheque_opened_at, 'dd.MM.yy HH:mm')}</td>
														<td className="text-end">{formatMoney(item.total_amount_service)}</td>
													</tr>
												))
												: (
													<tr>
														<td colSpan={5} className="text-center text-muted">{t('list_empty')}</td>
													</tr>
												)
											}
										</tbody>
										{!!operatorCheques?.data?.length &&
											<tfoot>
												<tr>
													<td colSpan={4}><b>{t('total')}</b></td>
													<td className="text-end">
														<b>{formatMoney(operatorCheques.total_cheques_amount)}</b>
													</td>
												</tr>
											</tfoot>
										}
									</table>
									{operatorChequesPagination.response &&
										<div className="mt-2">
											<FilterPagination
												pagination={operatorChequesPagination}
												paginate={paginateOperatorCheques}
											/>
										</div>
									}
								</>
							}
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</>
	)
}
